if (!customElements.get('product-modal')) {
  class LightboxZoom {
    constructor(modal) {
      this.modal = modal;
      this.stage = modal.stage;
      this.image = null;
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.maxScale = 4;
      this.doubleTapScale = 2.5;
      this.pinchStartDist = 0;
      this.pinchStartScale = 1;
      this.panStart = null;
      this.mousePan = null;
      this.touchStart = null;
      this.wasMultiTouch = false;
      this.lastTap = null;

      this.stage.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
      this.stage.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      this.stage.addEventListener('touchend', this.onTouchEnd.bind(this));
      this.stage.addEventListener('click', this.onClick.bind(this));
      this.stage.addEventListener('mousedown', this.onMouseDown.bind(this));
    }

    attach(image) {
      if (this.image !== image) this.reset();
      this.image = image || null;
    }

    reset() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.apply();
    }

    apply() {
      const zoomed = this.scale > 1.02;
      if (this.image) {
        this.image.style.transform = zoomed
          ? `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`
          : '';
        this.image.classList.toggle('is-zoomed', zoomed);
      }
      this.stage.classList.toggle('is-zoomed', zoomed);
      if (this.modal.prevButton) this.modal.prevButton.hidden = zoomed;
      if (this.modal.nextButton) this.modal.nextButton.hidden = zoomed;
      if (this.modal.counter) this.modal.counter.hidden = zoomed;
    }

    clampTranslate() {
      const rect = this.stage.getBoundingClientRect();
      const maxX = Math.max(0, (rect.width * (this.scale - 1)) / 2);
      const maxY = Math.max(0, (rect.height * (this.scale - 1)) / 2);
      this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
      this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
    }

    distance(touches) {
      const [a, b] = touches;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    onTouchStart(event) {
      if (!this.image) return;
      if (event.touches.length === 2) {
        event.preventDefault();
        this.pinchStartDist = this.distance(event.touches);
        this.pinchStartScale = this.scale;
        this.panStart = null;
        this.wasMultiTouch = true;
        this.touchStart = null;
      } else if (event.touches.length === 1) {
        this.wasMultiTouch = false;
        this.touchStart = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
          time: Date.now(),
        };
        if (this.scale > 1) {
          this.panStart = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
            tx: this.translateX,
            ty: this.translateY,
          };
        }
      }
    }

    onTouchMove(event) {
      if (!this.image) return;
      if (event.touches.length === 2) {
        event.preventDefault();
        const dist = this.distance(event.touches);
        const ratio = dist / this.pinchStartDist;
        this.scale = Math.min(this.maxScale, Math.max(1, this.pinchStartScale * ratio));
        this.clampTranslate();
        this.apply();
      } else if (this.panStart && this.scale > 1) {
        event.preventDefault();
        this.translateX = this.panStart.tx + (event.touches[0].clientX - this.panStart.x);
        this.translateY = this.panStart.ty + (event.touches[0].clientY - this.panStart.y);
        this.clampTranslate();
        this.apply();
      }
    }

    onTouchEnd(event) {
      const wasPanning = !!this.panStart;
      const wasMultiTouch = this.wasMultiTouch;
      this.panStart = null;
      if (this.scale <= 1.02) this.reset();
      if (event.touches.length === 0) this.wasMultiTouch = false;

      const start = this.touchStart;
      this.touchStart = null;
      if (!this.image || wasPanning || wasMultiTouch || event.touches.length > 0 || !start) return;

      const touch = event.changedTouches[0];
      if (!touch || touch.target !== this.image) return;

      const moved = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
      const duration = Date.now() - start.time;
      if (moved > 10 || duration > 300) return;

      // This was a plain tap on the image: swallow the browser's synthetic
      // click (suppressed by preventDefault below) and require a second tap
      // nearby, in quick succession, before treating it as double-tap-zoom.
      event.preventDefault();

      const now = Date.now();
      const last = this.lastTap;
      this.lastTap = { time: now, x: touch.clientX, y: touch.clientY };
      if (last && now - last.time < 350 && Math.hypot(touch.clientX - last.x, touch.clientY - last.y) < 40) {
        this.lastTap = null;
        this.toggleZoom(touch.clientX, touch.clientY);
      }
    }

    onMouseDown(event) {
      if (!this.image || event.target !== this.image || this.scale <= 1) return;
      event.preventDefault();
      this.mousePan = {
        x: event.clientX,
        y: event.clientY,
        tx: this.translateX,
        ty: this.translateY,
      };
      this.didPan = false;
      this.image.classList.add('is-panning');

      const onMove = (moveEvent) => {
        if (!this.mousePan) return;
        const dx = moveEvent.clientX - this.mousePan.x;
        const dy = moveEvent.clientY - this.mousePan.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.didPan = true;
        this.translateX = this.mousePan.tx + dx;
        this.translateY = this.mousePan.ty + dy;
        this.clampTranslate();
        this.apply();
      };
      const onUp = () => {
        this.mousePan = null;
        if (this.image) this.image.classList.remove('is-panning');
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }

    onClick(event) {
      if (event.target !== this.image) return;
      // A drag-to-pan ends with a mouseup on the image, which still fires a
      // trailing click here. Swallow it so panning doesn't un-zoom itself.
      if (this.didPan) {
        this.didPan = false;
        return;
      }
      this.toggleZoom(event.clientX, event.clientY);
    }

    toggleZoom(clientX, clientY) {
      if (!this.image) return;
      if (this.scale > 1) {
        this.reset();
        return;
      }
      const rect = this.stage.getBoundingClientRect();
      const originX = clientX - rect.left - rect.width / 2;
      const originY = clientY - rect.top - rect.height / 2;
      this.scale = this.doubleTapScale;
      this.translateX = -originX * (this.scale - 1);
      this.translateY = -originY * (this.scale - 1);
      this.clampTranslate();
      this.apply();
    }
  }

  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();

      this.stage = this.querySelector('.product-media-modal__stage');
      this.prevButton = this.querySelector('.product-media-modal__nav--prev');
      this.nextButton = this.querySelector('.product-media-modal__nav--next');
      this.counter = this.querySelector('.product-media-modal__counter');
      this.counterCurrent = this.querySelector('.product-media-modal__counter-current');
      this.thumbnailsEl = this.querySelector('.product-media-modal__thumbnails');
      this.thumbnails = Array.from(this.querySelectorAll('.product-media-modal__thumb'));
      this.dots = Array.from(this.querySelectorAll('.product-media-modal__dot'));
      this.items = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      this.currentIndex = 0;

      this.zoom = new LightboxZoom(this);

      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goToMediaId(thumb.getAttribute('data-media-id')));
        thumb.addEventListener('pointerup', (event) => event.stopPropagation());
      });

      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));
      [this.prevButton, this.nextButton, this.counter].forEach((el) =>
        el?.addEventListener('pointerup', (event) => event.stopPropagation())
      );
      this.thumbnailsEl?.addEventListener('pointerup', (event) => event.stopPropagation());

      // Only the image itself should swallow the click; the surrounding stage
      // is the "background" and should still dismiss the modal on tap.
      this.stage.addEventListener('pointerup', (event) => {
        if (event.target !== this.stage) event.stopPropagation();
      });
      this.stage.addEventListener('click', (event) => {
        if (event.target === this.stage) this.hide();
      });

      this.initSwipe();

      this.handleKeydown = (event) => {
        if (!this.hasAttribute('open') || this.zoom.scale > 1) return;
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      };

      // Capture Escape before it bubbles up to the base ModalDialog's keyup
      // handler, so while zoomed it only resets the zoom instead of closing.
      this.handleEscape = (event) => {
        if (event.code.toUpperCase() !== 'ESCAPE' || !this.hasAttribute('open')) return;
        if (this.zoom.scale > 1) {
          event.stopPropagation();
          this.zoom.reset();
        }
      };
    }

    connectedCallback() {
      // ModalDialog.connectedCallback() relocates this element to
      // document.body on its first connect (if it isn't already there),
      // which disconnects and reconnects it synchronously - wiping out any
      // document listeners added in the constructor via disconnectedCallback
      // before they ever get used. Attaching here instead means they're
      // (re)added on the connect that actually sticks.
      super.connectedCallback();
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('keyup', this.handleEscape, true);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('keyup', this.handleEscape, true);
    }

    initSwipe() {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      this.stage.addEventListener(
        'touchstart',
        (event) => {
          if (this.zoom.scale > 1 || event.touches.length !== 1) return;
          tracking = true;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.stage.addEventListener('touchend', (event) => {
        if (!tracking) return;
        tracking = false;
        const endX = event.changedTouches[0].clientX;
        const endY = event.changedTouches[0].clientY;
        const dx = endX - startX;
        const dy = endY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.step(dx < 0 ? 1 : -1);
        }
      });
    }

    show(opener) {
      this.scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';

      super.show(opener);
      this.showActiveMedia();
    }

    followThumbnailScroll(index) {
      const list = this.thumbnailsEl;
      if (!list) return;
      const items = this.thumbnails;
      if (items.length <= 1 || list.scrollHeight <= list.clientHeight) return;

      const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
      const rowHeight = items[0].getBoundingClientRect().height + gap;
      if (!rowHeight) return;

      const visibleCount = Math.max(1, Math.round(list.clientHeight / rowHeight));
      const currentTop = Math.round(list.scrollTop / rowHeight);
      const currentBottom = currentTop + visibleCount - 1;
      const maxTop = Math.max(0, items.length - visibleCount);

      let targetRow = null;
      if (index > currentBottom || index < currentTop) {
        targetRow = Math.min(maxTop, Math.max(0, index - visibleCount + 2));
      } else if (index >= currentBottom - 1 && currentBottom < items.length - 1) {
        targetRow = currentTop + 1;
      } else if (index <= currentTop + 1 && currentTop > 0) {
        targetRow = currentTop - 1;
      }
      if (targetRow !== null) list.scrollTo({ top: targetRow * rowHeight, behavior: 'smooth' });
    }

    hide() {
      super.hide();
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, this.scrollY || 0);
      this.zoom.reset();
    }

    showActiveMedia() {
      const targetId = this.openedBy?.getAttribute('data-media-id');
      const index = this.items.findIndex((item) => item.getAttribute('data-media-id') === targetId);
      this.goToIndex(index === -1 ? 0 : index);
    }

    goToMediaId(mediaId) {
      const index = this.items.findIndex((item) => item.getAttribute('data-media-id') === mediaId);
      if (index !== -1) this.goToIndex(index);
    }

    step(direction) {
      const next = this.currentIndex + direction;
      if (next < 0 || next >= this.items.length) return;
      this.goToIndex(next);
    }

    goToIndex(index) {
      if (index < 0 || index >= this.items.length) return;
      this.currentIndex = index;
      this.zoom.reset();

      this.items.forEach((item, i) => item.classList.toggle('active', i === index));
      this.thumbnails.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (this.counterCurrent) this.counterCurrent.textContent = index + 1;

      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.items.length - 1;

      this.followThumbnailScroll(index);

      const activeItem = this.items[index];
      const image = activeItem?.tagName === 'IMG' ? activeItem : activeItem?.querySelector('img') || null;
      this.zoom.attach(image);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('keyup', this.handleEscape, true);
    }
  });
}
